/**
 * ADR-0068 Phase 8: Admin surface for personal content owned by disabled
 * accounts. Spec §6, §9.7.
 *
 * Admin-only. Unions the five scoped tables via GET /tenant/admin/orphaned-content
 * and renders a table with per-row Reassign / Promote to shared / Delete.
 */

import { useEffect, useState } from "react";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import type { ScopedEntityKind } from "../lib/scope.js";

type OrphanedRow = {
  entity_kind: ScopedEntityKind;
  id: string;
  name: string;
  owner_account_id: string;
  owner_email: string | null;
  owner_display_name: string | null;
  created_at: string;
};

const URL_KIND: Record<ScopedEntityKind, string> = {
  page: "page",
  menu: "menu",
  pdo: "pdo",
  workflow: "workflow",
  saved_query: "saved_query",
  study: "study",
  optimize_model: "optimize_model"
};

const KIND_LABELS: Record<ScopedEntityKind, string> = {
  page: "Page",
  menu: "Menu",
  pdo: "PDO",
  workflow: "Schedule",
  saved_query: "Saved Query",
  study: "Study",
  optimize_model: "Optimization Model"
};

export function OrphanedContent() {
  const toast = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<OrphanedRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState<ScopedEntityKind | "all">("all");

  const [reassignTarget, setReassignTarget] = useState<OrphanedRow | null>(null);
  const [reassignAccountId, setReassignAccountId] = useState("");
  const [memberCandidates, setMemberCandidates] = useState<MemberCandidate[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const dir = await apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates");
        setMemberCandidates(dir.candidates.filter((c) => c.kind === "user"));
      } catch {}
    })();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<{ orphaned: OrphanedRow[] }>("/api/tenant/admin/orphaned-content");
      setRows(r.orphaned ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const visible = filterKind === "all" ? rows : rows.filter((r) => r.entity_kind === filterKind);

  async function handlePromoteToShared(row: OrphanedRow) {
    setBusy(`${row.entity_kind}:${row.id}`);
    try {
      await apiFetch(`/api/tenant/admin/orphaned-content/${URL_KIND[row.entity_kind]}/${encodeURIComponent(row.id)}/promote`, {
        method: "POST",
        body: { target_scope: "shared" }
      });
      toast.push({ kind: "success", message: `Promoted to shared.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof ApiRequestError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(row: OrphanedRow) {
    const ok = await confirm.confirm({
      title: `Delete this ${KIND_LABELS[row.entity_kind].toLowerCase()}?`,
      detail: `This permanently removes "${row.name}". Dependents will block the delete.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel"
    });
    if (!ok) return;
    setBusy(`${row.entity_kind}:${row.id}`);
    try {
      await apiFetch(`/api/tenant/admin/orphaned-content/${URL_KIND[row.entity_kind]}/${encodeURIComponent(row.id)}`, {
        method: "DELETE"
      });
      toast.push({ kind: "success", message: "Deleted." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof ApiRequestError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  async function submitReassign() {
    if (!reassignTarget || !reassignAccountId.trim()) return;
    setBusy(`${reassignTarget.entity_kind}:${reassignTarget.id}`);
    try {
      await apiFetch(
        `/api/tenant/admin/orphaned-content/${URL_KIND[reassignTarget.entity_kind]}/${encodeURIComponent(reassignTarget.id)}/reassign`,
        { method: "POST", body: { target_account_id: reassignAccountId.trim() } }
      );
      toast.push({ kind: "success", message: "Reassigned." });
      setReassignTarget(null);
      setReassignAccountId("");
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof ApiRequestError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <PageLayout title="Orphaned Content">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="muted" style={{ fontSize: 13 }}>
            Personal content whose owner is no longer an active member of this tenant. Reassign it
            to another user, promote it to shared, or delete it.
          </div>
        </div>

        <div className="toolbar">
          <div className="toolbarGroup">
            <label className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
              <span className="label" style={{ marginBottom: 0 }}>Kind</span>
              <select
                className="select"
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value as ScopedEntityKind | "all")}
              >
                <option value="all">All</option>
                <option value="page">Pages</option>
                <option value="menu">Menus</option>
                <option value="pdo">PDOs</option>
                <option value="workflow">Schedules</option>
                <option value="saved_query">Saved Queries</option>
              </select>
            </label>
          </div>
        </div>

        {error ? <ErrorState detail={error} /> : null}
        {loading ? (
          <LoadingState />
        ) : visible.length === 0 ? (
          <div className="muted" style={{ padding: "24px 12px" }}>No orphaned content in this tenant.</div>
        ) : (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Name</th>
                  <th>Owner (disabled)</th>
                  <th>Since</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const rowKey = `${r.entity_kind}:${r.id}`;
                  const isBusy = busy === rowKey;
                  return (
                    <tr key={rowKey}>
                      <td>{KIND_LABELS[r.entity_kind]}</td>
                      <td>{r.name}</td>
                      <td className="muted">{r.owner_display_name ?? r.owner_email ?? "—"}</td>
                      <td className="muted">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          className="adminListAction"
                          onClick={() => {
                            setReassignTarget(r);
                            setReassignAccountId("");
                          }}
                          disabled={isBusy}
                        >
                          Reassign…
                        </button>
                        {" · "}
                        <button
                          className="adminListAction"
                          onClick={() => void handlePromoteToShared(r)}
                          disabled={isBusy}
                        >
                          Promote to shared
                        </button>
                        {" · "}
                        <button
                          className="adminListAction isDanger"
                          onClick={() => void handleDelete(r)}
                          disabled={isBusy}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reassignTarget && (
        <div
          className="modalOverlay"
          role="presentation"
          onClick={busy ? undefined : () => setReassignTarget(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: 460, width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>Reassign {KIND_LABELS[reassignTarget.entity_kind].toLowerCase()}</h2>
              <div className="muted" style={{ fontSize: 12 }}>
                "{reassignTarget.name}" — current owner is inactive.
              </div>
            </div>
            <div style={{ padding: "14px 18px", display: "grid", gap: 12 }}>
              <div className="field">
                <span className="label">New owner</span>
                <MemberPicker
                  mode="single"
                  allowedKinds={["user"]}
                  candidates={memberCandidates}
                  selected={reassignAccountId ? [reassignAccountId] : []}
                  onChange={(next) => setReassignAccountId(next[0] ?? "")}
                  placeholder="Search active members…"
                />
                <div className="help">
                  The row will become personal content owned by the selected member.
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn" onClick={() => setReassignTarget(null)} disabled={busy != null}>Cancel</button>
              <button
                className="btn btnPrimary"
                onClick={() => void submitReassign()}
                disabled={busy != null || !reassignAccountId.trim()}
              >
                {busy ? "Reassigning…" : "Reassign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
